<template>
	<div class="container-fluid py-4">
		<div class="d-flex align-items-center mb-3">
			<RouterLink
				:to="routes.ReviewSite(id.toString())"
				class="btn btn-outline-secondary btn-sm me-3"
			>
				<i class="bi bi-arrow-left"></i> Back to Sites
			</RouterLink>
			<h4 class="mb-0">{{ title }}</h4>
			<button
				type="button"
				class="btn btn-primary btn-sm ms-auto"
				:disabled="!site"
				@click="showEdit = true"
			>
				<i class="bi bi-pencil me-1"></i>Edit
			</button>
			<button
				type="button"
				class="btn btn-outline-danger btn-sm ms-2"
				:disabled="!site"
				@click="showDelete = true"
			>
				<i class="bi bi-trash me-1"></i>Delete
			</button>
		</div>

		<div v-if="error" class="alert alert-danger">{{ error }}</div>
		<div v-else-if="loading" class="text-muted">Loading site…</div>
		<div v-else-if="site">
			<div class="row g-3">
				<div class="col-lg-7">
					<div class="card mb-3">
						<div class="card-header fw-semibold">Details</div>
						<div class="card-body">
							<div
								v-if="site.geometry_truncated"
								class="alert alert-warning d-flex align-items-start mb-3"
								role="alert"
							>
								<i class="bi bi-exclamation-triangle me-2"></i>
								<div>
									Site boundary truncated at import. The imported source shape
									has more than one part, so only the largest part is used as
									the Site boundary; the full shape is kept on the Feature for
									investigation.
								</div>
							</div>
							<table class="table mb-0">
								<tbody>
									<tr>
										<td style="width: 30%"><b>Address</b></td>
										<td>
											{{ formatAddress(site.address) }}
											{{ site.address.region }} {{ site.address.postal_code }}
										</td>
									</tr>
									<tr>
										<td><b>Owner</b></td>
										<td>{{ site.owner?.name || "—" }}</td>
									</tr>
									<tr>
										<td><b>Resident</b></td>
										<td>{{ site.resident?.name || "—" }}</td>
									</tr>
									<tr>
										<td><b>Parcel ID</b></td>
										<td>{{ site.parcel?.external_id || "—" }}</td>
									</tr>
									<tr>
										<td><b>Created</b></td>
										<td>{{ formatDate(new Date(site.created)) }}</td>
									</tr>
									<tr>
										<td><b>Notes</b></td>
										<td>{{ site.notes || "—" }}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>

					<div v-if="!site.parcel || hasParcelDetails" class="card mb-3">
						<div class="card-header fw-semibold">Parcel</div>
						<div class="card-body">
							<div v-if="!site.parcel" class="text-muted">No parcel</div>
							<table v-else class="table mb-0">
								<tbody>
									<tr>
										<td style="width: 30%"><b>Description</b></td>
										<td>{{ site.parcel.description || "—" }}</td>
									</tr>
									<tr>
										<td><b>Address (raw)</b></td>
										<td>{{ site.parcel.address_raw || "—" }}</td>
									</tr>
									<tr>
										<td><b>Property Type</b></td>
										<td>{{ site.parcel.property_type || "—" }}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>

					<div class="card mb-3">
						<div class="card-header fw-semibold">Features</div>
						<div class="card-body">
							<div v-if="featureBlocks.length === 0" class="text-muted">
								No features
							</div>
							<div v-else class="d-flex flex-column gap-3">
								<div v-for="feature in featureBlocks" :key="feature.id">
									<div class="fw-semibold">
										<RouterLink
											:to="{
												name: 'Feature Review',
												params: { id: feature.id.toString() },
											}"
										>
											#{{ feature.id }} — {{ featureTypeLabel(feature.type) }}
										</RouterLink>
									</div>

									<div v-if="feature.loading" class="text-muted small">
										<span
											class="spinner-border spinner-border-sm me-2"
											role="status"
											aria-hidden="true"
										></span>
										Loading…
									</div>
									<div v-else-if="feature.error" class="text-danger small">
										{{ feature.error }}
									</div>
									<template v-else>
										<div class="mt-2">
											<div class="text-muted small fw-semibold">Treatments</div>
											<div
												v-if="feature.treatments.length === 0"
												class="text-muted small"
											>
												None
											</div>
											<div v-else class="table-responsive">
												<table class="table table-sm mb-0">
													<thead>
														<tr>
															<th>Treatment ID</th>
															<th>Start Time</th>
															<th>Duration</th>
															<th>Method</th>
															<th>Area (m²)</th>
															<th>Quantity</th>
														</tr>
													</thead>
													<tbody>
														<tr
															v-for="treatment in feature.treatments"
															:key="treatment.id"
														>
															<td>
																<RouterLink
																	:to="{
																		name: 'Treatment Review Detail',
																		params: {
																			id: treatment.id.toString(),
																		},
																	}"
																>
																	{{ treatment.id }}
																</RouterLink>
															</td>
															<td>
																<TimeRelativeWithTooltip
																	:time="treatment.start_datetime"
																/>
															</td>
															<td>
																{{
																	formatDuration(
																		durationSeconds(
																			treatment.start_datetime,
																			treatment.end_datetime,
																		),
																	)
																}}
															</td>
															<td>{{ treatment.method || "—" }}</td>
															<td>
																{{ formatArea(treatment.area_treated) }}
															</td>
															<td>
																{{ formatQuantity(treatment.quantity) }}
																{{ treatment.quantity_unit || "" }}
															</td>
														</tr>
													</tbody>
												</table>
											</div>
										</div>

										<div class="mt-2">
											<div class="text-muted small fw-semibold">
												Inspections
											</div>
											<div
												v-if="feature.inspections.length === 0"
												class="text-muted small"
											>
												None
											</div>
											<div v-else class="table-responsive">
												<table class="table table-sm mb-0">
													<thead>
														<tr>
															<th>Inspection ID</th>
															<th>Start Time</th>
															<th>Duration</th>
															<th>Method</th>
															<th>Production</th>
															<th>Stage</th>
															<th>Density</th>
														</tr>
													</thead>
													<tbody>
														<tr
															v-for="inspection in feature.inspections"
															:key="inspection.id"
														>
															<td>
																<RouterLink
																	:to="{
																		name: 'Inspection Review Detail',
																		params: {
																			id: inspection.id.toString(),
																		},
																	}"
																>
																	{{ inspection.id }}
																</RouterLink>
															</td>
															<td>
																<TimeRelativeWithTooltip
																	:time="inspection.start_datetime"
																/>
															</td>
															<td>
																{{
																	formatDuration(
																		durationSeconds(
																			inspection.start_datetime,
																			inspection.end_datetime,
																		),
																	)
																}}
															</td>
															<td>{{ inspection.method || "—" }}</td>
															<td>
																{{ inspection.production ? "Yes" : "No" }}
															</td>
															<td>{{ inspection.stage || "—" }}</td>
															<td>{{ inspection.density || "—" }}</td>
														</tr>
													</tbody>
												</table>
											</div>
										</div>
									</template>
								</div>
							</div>
						</div>
					</div>

					<div class="card mb-3">
						<div class="card-header fw-semibold">Leads</div>
						<div class="card-body">
							<div v-if="site.leads.length === 0" class="text-muted">
								No leads
							</div>
							<table v-else class="table mb-0">
								<tbody>
									<tr v-for="lead in site.leads" :key="lead.id">
										<td>{{ lead.type }}</td>
										<td>
											<ul class="mb-0">
												<li
													v-for="crr in lead.compliance_report_requests"
													:key="crr.public_id"
												>
													<a
														:href="mailerLink(crr)"
														target="_blank"
														rel="noopener noreferrer"
													>
														Compliance Report Request {{ crr.public_id }}
													</a>
												</li>
											</ul>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>

				<div class="col-lg-5">
					<div class="card">
						<div class="card-header fw-semibold">Map</div>
						<div class="card-body">
							<div class="map-wrap">
								<MapLocator
									:markers="markers"
									:useSatellite="true"
									:geojson="parcelGeojson"
									v-model="mapCamera"
								></MapLocator>
							</div>
						</div>
					</div>

					<div class="card mt-3">
						<div class="card-header fw-semibold">Situs (Assessment)</div>
						<div class="card-body">
							<div v-if="!site.parcel || !site.parcel.situs" class="text-muted">
								No situs data
							</div>
							<div v-else-if="situsRows.length === 0" class="text-muted">
								No situs fields
							</div>
							<table v-else class="table mb-0">
								<tbody>
									<tr v-for="(row, i) in situsRows" :key="i">
										<td style="width: 30%">
											<b>{{ row.label }}</b>
										</td>
										<td>{{ row.value }}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
			</div>

			<SiteEditModal
				:site="site"
				:show="showEdit"
				@close="showEdit = false"
				@saved="onSiteSaved"
			/>

			<!-- The Delete confirm: the endpoint soft-deletes, so the site only
			     disappears — the wording says so, since this UI offers no way
			     back. -->
			<div
				class="modal fade"
				:class="{ 'show d-block': showDelete }"
				tabindex="-1"
				v-show="showDelete"
				@click.self="showDelete = false"
			>
				<div class="modal-dialog modal-dialog-centered">
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">
								<i class="bi bi-exclamation-triangle me-2"></i>Delete Site
							</h5>
							<button
								type="button"
								class="btn-close"
								@click="showDelete = false"
							></button>
						</div>
						<div class="modal-body">
							<p>
								Hide <strong>{{ title }}</strong
								>?
							</p>
							<p class="mb-0 text-muted">
								The site disappears from every list here. Hiding it cannot be
								undone from this UI.
							</p>
						</div>
						<div class="modal-footer">
							<button
								type="button"
								class="btn btn-outline-secondary"
								@click="showDelete = false"
							>
								Cancel
							</button>
							<ButtonLoading
								@click="onSiteDelete"
								:loading="deleting"
								icon="bi-trash"
								text="Delete"
								variant="danger"
							/>
						</div>
					</div>
				</div>
			</div>
			<div
				class="modal-backdrop fade show"
				v-show="showDelete"
				@click="showDelete = false"
			></div>
		</div>
	</div>

	<ToastNotification
		v-model:show="toastShow"
		:title="toastTitle"
		:message="toastMessage"
	/>
</template>

<style scoped lang="scss">
.map-wrap {
	height: 500px;
	border-radius: 8px;
	overflow: hidden;
	box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}
</style>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import MapLocator from "@/components/MapLocator.vue";
import SiteEditModal from "@/components/SiteEditModal.vue";
import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import {
	durationSeconds,
	formatAddress,
	formatDate,
	formatDuration,
} from "@/format";
import { featureTypeLabel } from "@/lib/featureType";
import { rawSitusRows, type SitusDisplayRow } from "@/lib/situs";
import { router } from "@/route/config";
import { ROUTE_NAMES } from "@/route/name";
import { useRoutes } from "@/route/use";
import { useFeatureStore } from "@/store/feature";
import { useStoreSite } from "@/store/site";
import {
	ComplianceReportRequest,
	Feature,
	Site,
	type Inspection,
	type Treatment,
} from "@/type/api";
import { Camera } from "@/type/map";
import type { Marker } from "@/types";
import type { GeoJSON } from "geojson";

interface Props {
	id: number;
}
const props = defineProps<Props>();

const routes = useRoutes();
const storeSite = useStoreSite();
const loading = ref<boolean>(true);
const error = ref<string>("");
const site = ref<Site | undefined>(undefined);
const showEdit = ref<boolean>(false);
const mapCamera = ref<Camera>(new Camera());

// The page title: the structured number + street (with locality when known),
// else the raw address line, else "Site <id>". Falls back to the id until the
// record loads.
const title = computed<string>(() => {
	const address = site.value?.address;
	if (!address) {
		return `Site ${props.id}`;
	}
	const streetLine = `${address.number.trim()} ${address.street.trim()}`.trim();
	if (streetLine) {
		const locality = address.locality.trim();
		return locality ? `${streetLine}, ${locality}` : streetLine;
	}
	return address.raw.trim() || `Site ${props.id}`;
});

const markers = computed<Marker[]>(() => {
	const s = site.value;
	if (!s?.address.location) {
		return [];
	}
	const out: Marker[] = [
		{
			color: "#FF0000",
			draggable: false,
			id: "address",
			location: s.address.location,
		},
	];
	for (const feature of s.features) {
		out.push({
			color: "#00FF00",
			draggable: false,
			id: feature.id.toString(),
			location: feature.location,
		});
	}
	return out;
});

// Parcel boundary geometry (only present on the site detail endpoint).
const parcelGeojson = computed<GeoJSON.Geometry | null>(
	() => site.value?.parcel?.geometry_geojson ?? null,
);

function hasText(value: string | null | undefined): boolean {
	return (value ?? "").trim().length > 0;
}

// The Situs panel shows the normalized typed columns first, then every raw_data
// entry — a county may store most of its assessment detail only in raw_data, so
// the typed fields alone leave the panel looking empty. Rows with no value are
// omitted rather than rendered as em dashes.
const situsRows = computed<SitusDisplayRow[]>(() => {
	const situs = site.value?.parcel?.situs;
	if (!situs) {
		return [];
	}
	const rows: SitusDisplayRow[] = [];
	const add = (label: string, value: string | number | null | undefined) => {
		if (value == null) return;
		const text =
			typeof value === "number" ? value.toLocaleString() : value.trim();
		if (text === "") return;
		rows.push({ label, value: text });
	};
	add("Address", situs.address_raw);
	add("Lot Size (acres)", situs.lot_size_acres);
	add("Lot Size (sq ft)", situs.lot_size_sqft);
	add("Year Built", situs.year_built);
	add("Bedrooms", situs.bedrooms);
	if (situs.full_baths != null || situs.half_baths != null) {
		add("Baths", `${situs.full_baths ?? 0} (${situs.half_baths ?? 0} half)`);
	}
	add("Building Sq Ft", situs.building_sqft);
	add("Units", situs.num_units);
	add("Property Type", situs.property_type);
	add("Net Assessed Value", situs.net_assessed_value);
	add("Land Value", situs.land_value);
	add("Fireplaces", situs.fireplace_count);
	add("Pools", situs.pool_count);
	add("Tax Rate Area", situs.tax_rate_area);
	for (const row of rawSitusRows(situs.raw_data)) {
		rows.push(row);
	}
	return rows;
});

// The Parcel panel only earns its space when it shows more than the parcel ID,
// which the Details panel already lists. Hide it when description, address, and
// property type are all empty. A site with no parcel at all still shows the
// "No parcel" notice, which is not redundant with the Details panel.
const hasParcelDetails = computed<boolean>(() => {
	const parcel = site.value?.parcel;
	if (!parcel) {
		return false;
	}
	return [parcel.description, parcel.address_raw, parcel.property_type].some(
		hasText,
	);
});

function mailerLink(crr: ComplianceReportRequest): string {
	return `/mailer/mode-3/${crr.public_id}/preview`;
}

const storeFeature = useFeatureStore();

// One state entry per feature URI. Each feature detail is fetched on its own so
// a slow or failing one neither blocks nor breaks the others, and the card
// renders `featureBlocks` — site.features joined with those states — so the
// Features section keeps the site response's order. `type` starts as the
// embedded feature's type and is upgraded by the fetched detail; on failure it
// stays the embedded value, which still labels the header link.
interface FeatureState {
	loading: boolean;
	error: string;
	type: string;
	treatments: Treatment[];
	inspections: Inspection[];
}

interface FeatureBlock extends FeatureState {
	id: number;
}

// State is keyed by feature id (always present on the embedded feature) rather
// than by the URI, so a block stays distinct even when a URI is missing.
const featureStates = ref<Map<number, FeatureState>>(new Map());

const featureBlocks = computed<FeatureBlock[]>(() => {
	return (site.value?.features ?? []).map((feature) => {
		const state = featureStates.value.get(feature.id);
		return {
			id: feature.id,
			loading: state?.loading ?? true,
			error: state?.error ?? "",
			type: state?.type || feature.type,
			treatments: state?.treatments ?? [],
			inspections: state?.inspections ?? [],
		};
	});
});

// featureIDFromURI reads the id out of a feature's API URI ("/api/feature/{id}").
function featureIDFromURI(uri: string | undefined): number | null {
	if (!uri) {
		return null;
	}
	const segment = uri
		.split("/")
		.filter((part) => part.length > 0)
		.pop();
	if (segment === undefined) {
		return null;
	}
	const id = Number(segment);
	return Number.isInteger(id) ? id : null;
}

function setFeatureState(id: number, state: FeatureState): void {
	featureStates.value.set(id, state);
}

async function loadFeature(feature: Feature): Promise<void> {
	// Fall back to the embedded id when the URI is absent (e.g. a payload from a
	// backend predating feature URIs), so the card still loads the detail.
	const id = featureIDFromURI(feature.uri) ?? feature.id;
	setFeatureState(id, {
		loading: true,
		error: "",
		type: feature.type,
		treatments: [],
		inspections: [],
	});
	try {
		const detail = await storeFeature.fetchByID(id);
		setFeatureState(id, {
			loading: false,
			error: "",
			type: detail.type || feature.type,
			treatments: detail.treatments ?? [],
			inspections: detail.inspections ?? [],
		});
	} catch (err) {
		setFeatureState(id, {
			loading: false,
			error: err instanceof Error ? err.message : "Failed to load feature",
			type: feature.type,
			treatments: [],
			inspections: [],
		});
	}
}

// Fetch every feature detail in parallel; each block shows its own spinner.
function loadFeatures(features: Feature[]): void {
	void Promise.all(features.map(loadFeature));
}

const formatArea = (area: number | null): string => {
	return area === null ? "—" : area.toLocaleString();
};

const formatQuantity = (quantity: number | null): string => {
	return quantity === null ? "—" : quantity.toLocaleString();
};

// --- Toast (the view's own report of an edit's outcome) ---
const toastShow = ref(false);
const toastTitle = ref("");
const toastMessage = ref("");

function showToast(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

// The edit modal writes the four fields and nothing else; the refetch is what
// pulls the facts the server derives from them — the contacts behind the owner
// and resident names, the roll-up through the features, the new address the
// saved gid resolved to — so the page shows the stored record, not the form.
async function onSiteSaved(): Promise<void> {
	showEdit.value = false;
	try {
		const updated: Site = await storeSite.fetchOne(props.id);
		site.value = updated;
		const features = updated.features ?? [];
		if (features.length > 0) {
			loadFeatures(features);
		}
		showToast("Site updated", "The site's changes were saved.");
	} catch (err) {
		showToast(
			"Refresh failed",
			err instanceof Error ? err.message : "Failed to reload the site",
		);
	}
}

// --- Delete (the endpoint soft-deletes, so the site is only hidden: the
// confirm says so, since nothing in this UI can bring it back) ---
const showDelete = ref<boolean>(false);
const deleting = ref<boolean>(false);

async function onSiteDelete(): Promise<void> {
	const current = site.value;
	if (!current || deleting.value) {
		return;
	}
	deleting.value = true;
	try {
		await apiClient.JSONDelete(current.uri);
		// Drop the cached record so the list this returns to cannot keep a row
		// for a site the API no longer reads back.
		storeSite.remove(props.id);
		showToast("Site deleted", "The site was hidden from the sites list.");
		router.push({ name: ROUTE_NAMES.REVIEW_SITE });
	} catch (err) {
		// Close first: the view's toast renders beneath a modal backdrop.
		showDelete.value = false;
		showToast(
			"Delete failed",
			err instanceof Error ? err.message : "Failed to delete the site",
		);
	} finally {
		deleting.value = false;
	}
}

onMounted(async () => {
	try {
		// Always fetch the full record: parcel + situs data is only included on
		// the site detail endpoint, not the list response the cache holds.
		site.value = await storeSite.fetchOne(props.id);
	} catch (err) {
		error.value = err instanceof Error ? err.message : "Unknown error";
	} finally {
		loading.value = false;
	}
	const features = site.value?.features ?? [];
	if (features.length > 0) {
		loadFeatures(features);
	}
});
</script>
